import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, notFound } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { OrganizationOverviewView } from "~/components/cco-overview/organization-overview";
import { ButtonLink } from "~/components/ui/button";
import { PageHeader } from "~/components/ui/page-title";
import { CCO_TYPE_DISPLAY_NAME_SINGULAR } from "~/lib/ccos";
import { organizationOverviewQueryOptions } from "~/lib/functions/cco-overview";
import type { CCOType } from "~/lib/schemas/db-enums";

import { OrganizationMark } from "../-organization-mark";

const organizationRoute = getRouteApi("/_user/my-organization/$slug");

export const Route = createFileRoute("/_user/my-organization/$slug/")({
  staticData: { title: "Overview" },
  loader: async ({ context, params }) => {
    const overview = await context.queryClient.ensureQueryData(
      organizationOverviewQueryOptions(params.slug),
    );
    if (!overview) throw notFound();
  },
  component: OrganizationOverviewPage,
});

function representativesLine(others: number): string {
  if (others === 0)
    return "You represent this organization on Dream Coach Match.";
  return `You and ${others} other representative${others === 1 ? "" : "s"} represent this organization on Dream Coach Match.`;
}

function OrganizationOverviewPage(): React.JSX.Element {
  const { slug } = Route.useParams();
  const { organization } = organizationRoute.useLoaderData();
  const { data: overview } = useSuspenseQuery(
    organizationOverviewQueryOptions(slug),
  );
  const typeLabel =
    CCO_TYPE_DISPLAY_NAME_SINGULAR[organization.type as CCOType] ??
    "Organization";

  return (
    <div className="space-y-8">
      <div className="flex items-start gap-4">
        <OrganizationMark
          name={organization.name}
          logoUrl={organization.logoUrl}
        />
        <PageHeader
          className="flex-1"
          eyebrow={typeLabel}
          title={organization.name}
          description={representativesLine(organization.otherRepresentatives)}
          actions={
            <ButtonLink
              href={`/organizations/${organization.slug}`}
              target="_blank"
              variant="default"
            >
              View public page
              <ExternalLink className="ml-1.5 inline size-4" />
            </ButtonLink>
          }
        />
      </div>
      {overview && (
        <OrganizationOverviewView
          organization={organization}
          overview={overview}
        />
      )}
    </div>
  );
}
